import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bootstrap, PlatformProvider, usePlatform, type PlatformState } from '@cyntox/common/platform';
import { APP_LABELS, type AppKey } from '@cyntox/common/AppShell';
import { api } from '../api/client';
import { getAuthConfig } from '../api/auth';
import type { InventoryReference, LocationType } from '../api/types';

/* ---------------------------------------------------------------------------
   The signed-in person and their access come from the shared platform. This
   adds what Inventory's screens need: regions, groups, location types with
   their count lists, supplies and locations — and today's business date,
   which comes from the API so every screen agrees on which week it is.
   --------------------------------------------------------------------------- */

interface AppState extends PlatformState {
  reference: InventoryReference;
  regionName: (regionCode: string) => string;
  groupName: (groupId: number) => string;
  typeOf: (locationTypeId: number) => LocationType | undefined;
  /** Setup changes what every region counts, so changing it needs Setup at all regions. */
  canEditSetup: boolean;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used inside <AppProvider>');
  return v;
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <PlatformProvider appLabel="Inventory">
      <InventoryProvider>{children}</InventoryProvider>
    </PlatformProvider>
  );
}

function InventoryProvider({ children }: { children: ReactNode }) {
  const platform = usePlatform();
  const hasAccess = platform.can('counts', 'view') || platform.can('setup', 'view');
  const refQuery = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.get<InventoryReference>('/api/reference'),
    enabled: hasAccess,
  });

  const value = useMemo<AppState | null>(() => {
    const reference = refQuery.data;
    if (!reference) return null;
    return {
      ...platform,
      reference,
      regionName: (code) => reference.regions.find((r) => r.regionCode === code)?.name ?? code,
      groupName: (id) => reference.groups.find((g) => g.groupId === id)?.name ?? `Group ${id}`,
      typeOf: (id) => reference.types.find((t) => t.locationTypeId === id),
      canEditSetup: platform.can('setup', 'edit') && platform.locationsFor('setup', 'edit') === null,
    };
  }, [platform, refQuery.data]);

  // Signed in with a role, but none of it is Inventory's: point to where it leads.
  if (!hasAccess) return <NothingHere />;

  if (refQuery.isError) {
    return (
      <Bootstrap title="Could not load Inventory">
        <p>{(refQuery.error as Error).message}</p>
      </Bootstrap>
    );
  }
  if (!value) return <Bootstrap title="Loading Inventory…">{null}</Bootstrap>;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function NothingHere() {
  const apps = getAuthConfig()?.apps ?? {};
  const others = (Object.keys(APP_LABELS) as AppKey[]).filter((key) => key !== 'inventory' && apps[key]);
  return (
    <Bootstrap title="Nothing in Inventory for your access">
      <p>Your role doesn't include any of Inventory's screens. An administrator gives Inventory access by region in Admin.</p>
      {others.length > 0 && (
        <p>
          {others.map((key, i) => (
            <span key={key}>{i > 0 && ' · '}<a href={apps[key]}>{APP_LABELS[key]}</a></span>
          ))}
        </p>
      )}
    </Bootstrap>
  );
}
